import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useUserOrNull } from "shared-ts-utils-authentication";
import { useAppInfoOrNull } from "shared-ts-app-info";
import { UserSettingsContext } from "./UserSettingsContext";
import { SettingKey } from "./SettingKeyDto";
import { NonAsyncSettingsStorage } from "./storage/NonAsyncSettingsStorage";
import { AsyncSettingsStorage } from "./storage/AsyncSettingsStorage";
import { ObjectUtils, UnsupportedError } from "shared-ts-utils-core";
import { SettingConfig } from "./SettingConfig";
import { SettingOverrides } from "./SettingOverrides";
import { useMemoizeSettingConfig } from "./useMemoizeSettingConfig";

export type UseUserSettingWithDefaultReturnType<TValue> = [
  TValue,
  Dispatch<SetStateAction<TValue>>
];

export const useUserSettingWithDefault = <TValue, TDefaultValue = TValue>(
  name: string,
  settingConfig: SettingConfig<TValue>,
  defaultValue: TDefaultValue,
  overrides?: SettingOverrides<TValue>
): UseUserSettingWithDefaultReturnType<TValue> => {
  const memoizedSettingConfig = useMemoizeSettingConfig(settingConfig);
  const appInfo = useAppInfoOrNull();
  const user = useUserOrNull();
  const { settings, setSettings, storage, localStorage } =
    useContext(UserSettingsContext);

  const settingKey = useMemo(
    () => SettingKey.from(name, appInfo?.key, user?.id ?? null),
    [name, appInfo?.key, user?.id]
  );

  let valueAsString = settings[settingKey.asString] ?? null;

  if (overrides?.storeLocally === true) {
    valueAsString = localStorage.readRawValue(
      settingKey,
      memoizedSettingConfig,
      overrides
    );
  }

  const value = useDeserializeValue(
    valueAsString,
    defaultValue,
    overrides,
    memoizedSettingConfig
  );

  const setValue_: Dispatch<SetStateAction<TValue>> = (
    newValueOrFn: SetStateAction<TValue>
  ) => {
    const prevValue = value;
    const prevValueSerialized = valueAsString;
    const newValue = resolveNewValue(prevValue, newValueOrFn);
    const newValueSerialized =
      newValue != null
        ? overrides?.serialize != null
          ? overrides.serialize(newValue)
          : memoizedSettingConfig.serialize(newValue)
        : null;

    const isChange = !ObjectUtils.equals(
      prevValueSerialized,
      newValueSerialized
    );
    if (isChange) {
      setSettings((prev) => {
        return {
          ...prev,
          [settingKey.asString]: newValueSerialized,
        };
      });

      if (overrides?.storeLocally === true) {
        localStorage.writeValue(
          settingKey,
          newValue,
          memoizedSettingConfig,
          overrides
        );
      } else if (storage.type === "NonAsync") {
        const nonAsyncStorage = storage as NonAsyncSettingsStorage;
        nonAsyncStorage.writeValue(
          settingKey,
          newValue,
          memoizedSettingConfig,
          overrides
        );
      } else if (storage.type === "Async") {
        const asyncStorage = storage as AsyncSettingsStorage;
        asyncStorage.writeValue(
          settingKey,
          newValue,
          memoizedSettingConfig,
          overrides
        );
      } else {
        throw new UnsupportedError(
          "Unsupported SettingsStorage.TValue: " + storage.type
        );
      }

      if (overrides?.storeCopyToAppLocally === true) {
        const appSettingKey = SettingKey.from(name, appInfo?.key, null);
        localStorage.writeValue(
          appSettingKey,
          newValue,
          memoizedSettingConfig,
          overrides
        );
      }
    }
  };

  if (value != null) {
    return [value, setValue_];
  }

  return [defaultValue as unknown as TValue, setValue_];
};

const useDeserializeValue = <TValue, TDefaultValue>(
  valueAsString: string | null,
  defaultValue: TDefaultValue,
  overrides: SettingOverrides<TValue> | undefined,
  settingConfig: SettingConfig<TValue>
): TValue => {
  return useMemo(() => {
    if (valueAsString == null) {
      return defaultValue as unknown as TValue;
    }

    if (overrides?.deserialize != null) {
      return overrides.deserialize(valueAsString);
    }

    return settingConfig.deserialize(valueAsString);
  }, [valueAsString, overrides, settingConfig, defaultValue]);
};

const resolveNewValue = <TValue>(
  prevValue: TValue,
  newValueOrFn: SetStateAction<TValue>
): TValue => {
  let newValue: TValue;
  if (typeof newValueOrFn === "function") {
    const fn: (prevState: TValue) => TValue = newValueOrFn as (
      prevState: TValue
    ) => TValue;
    newValue = fn(prevValue);
  } else {
    newValue = newValueOrFn;
  }
  return newValue;
};
